/**
 * 入口分流用的示範資料（鋼瓶 barcode、既有客戶名單）。
 * 目前為寫死清單，之後 supplier / code / 近期訂單 / 既有客戶查詢皆改為 API。
 */
import { maskPhone } from "@utils/func/maskPhone";

/** 確認身分卡上的單一配送地址（遮罩；後端 addresses[]）。 */
export interface MatchedAddressItem {
  address_masked: string;
  is_represent: boolean;
}

export interface CylinderCustomer {
  name: string;
  phone: string;
  /** 主要／代表地址遮罩（相容舊欄位；多地址時等於 addresses 中 is_represent 那筆）。 */
  addr: string;
  hasOrder: boolean;
  /** 全部配送地址（遮罩）；有值時確認卡應全部列出並 highlight 主要。 */
  addresses?: MatchedAddressItem[];
}

export interface CylinderInfo {
  state: "matched" | "joined" | "invalid" | "new";
  cust?: CylinderCustomer;
}

export const CYLINDER_DB: Record<string, CylinderInfo> = {
  "TW-2024-8801": { state: "matched", cust: { name: "王＊明", phone: "09＊＊-＊＊＊-678", addr: "台中市西屯區＊＊路 20 號", hasOrder: false } },
  "TW-2024-8805": { state: "matched", cust: { name: "李＊華", phone: "09＊＊-＊＊＊-321", addr: "台中市南屯區＊＊街 8 號", hasOrder: true } },
  "TW-2024-8802": { state: "joined" },
  "TW-2024-8803": { state: "invalid" },
  "TW-2024-8804": { state: "new" },
};

export const resolveBarcode = (code?: string | null): CylinderInfo =>
  (code && CYLINDER_DB[code]) || { state: "new" };

export const EXISTING_PHONES = ["0916375136", "0912345678"];

export const isExistingPhone = (p?: string): boolean =>
  EXISTING_PHONES.includes((p || "").replace(/\D/g, ""));

// maskPhone 統一改用中央工具（前2後3）；此處 re-export 讓既有 importers（ExistingPhone / NpOtp）不受影響。
export { maskPhone };

/** 無指定瓦斯行時，用地址比對是否找得到所屬瓦斯行（demo：含這些行政區視為找到）。 */
export const isKnownAddress = (a?: string): boolean => /西屯|南屯|大安/.test(a || "");

/**
 * 無指定瓦斯行時，用手機號碼找出所屬瓦斯行（demo 對照）。
 * 一支手機可能對應多家（在不同瓦斯行都叫過桶）；各帶最近叫桶天數，供排序/顯示。
 */
export interface MatchedSupplier {
  id: string;
  lastOrderDays: number;
}
const PHONE_TO_SUPPLIERS: Record<string, MatchedSupplier[]> = {
  "0916375136": [{ id: "S001", lastOrderDays: 3 }], // 單一
  "0912345678": [
    { id: "S002", lastOrderDays: 5 },
    { id: "S001", lastOrderDays: 22 },
  ], // 多家 → 按最近叫桶排序
};
/** 回傳比對到的瓦斯行清單，已按「最近叫桶」由近到遠排序。 */
export const findSuppliersByPhone = (p?: string): MatchedSupplier[] =>
  (PHONE_TO_SUPPLIERS[(p || "").replace(/\D/g, "")] || []).slice().sort((a, b) => a.lastOrderDays - b.lastOrderDays);

/** 用手機號碼找回既有客戶（遮罩資料），供「這是您嗎」確認卡顯示。 */
const PHONE_CUST: Record<string, { name: string; addr: string; hasOrder: boolean }> = {
  "0916375136": { name: "王＊明", addr: "台中市西屯區＊＊路 20 號", hasOrder: false },
  "0912345678": { name: "李＊華", addr: "台中市南屯區＊＊街 8 號", hasOrder: true },
};
export const findCustomerByPhone = (p?: string): CylinderCustomer | null => {
  const d = (p || "").replace(/\D/g, "");
  const c = PHONE_CUST[d];
  return c ? { name: c.name, phone: maskPhone(d), addr: c.addr, hasOrder: c.hasOrder } : null;
};

/** 把 match API 的 customer 轉成確認卡用的 CylinderCustomer（含 addresses）。 */
export function toMatchedCustomer(input: {
  phone: string;
  address_masked?: string | null;
  addresses?: Array<{ address_masked: string; is_represent: boolean }> | null;
  hasOrder?: boolean;
  name?: string;
}): CylinderCustomer {
  const addresses: MatchedAddressItem[] = (input.addresses ?? [])
    .filter((a) => !!a.address_masked)
    .map((a) => ({ address_masked: a.address_masked, is_represent: !!a.is_represent }));
  const primary =
    addresses.find((a) => a.is_represent)?.address_masked ||
    input.address_masked ||
    addresses[0]?.address_masked ||
    "";
  return {
    name: input.name || "既有客戶",
    phone: input.phone,
    addr: primary,
    hasOrder: input.hasOrder ?? false,
    addresses: addresses.length > 0 ? addresses : primary ? [{ address_masked: primary, is_represent: true }] : undefined,
  };
}
